import { dateTimeFormatterByTimezone } from '@/utils/formatter';
import {
    startOfWeek, endOfWeek, subWeeks, intervalToDuration, parseISO, isValid
} from 'date-fns';
import type { Duration } from 'date-fns';
import type { WeekDay } from '@/components/TeamAndSchedule/composables/useWeekStartDay';

// weekStartDay - 0 = Sunday, 1 = Monday, 6 = Saturday
export const calculateLastWeekDates = (weekStartsOn: WeekDay = 1) => {
    const startOfLastWeek = startOfWeek(subWeeks(new Date(), 1), { weekStartsOn });
    const endOfLastWeek = endOfWeek(subWeeks(new Date(), 1), { weekStartsOn });

    const startFormatted = new Date(
        startOfLastWeek.setHours(0, 0, 0, 0)
    ).toISOString();

    const endFormatted = new Date(
        endOfLastWeek.setHours(23, 59, 59, 999)
    ).toISOString();

    return `${startFormatted},${endFormatted}`;
};

export const calculateCurrentWeekDates = (weekStartsOn: WeekDay = 1) => {
    const today = new Date();

    // Start of current week based on weekStartsOn parameter
    const startOfCurrentWeek = startOfWeek(today, { weekStartsOn });
    const endOfCurrentWeek = endOfWeek(today, { weekStartsOn });

    const startFormatted = new Date(
        startOfCurrentWeek.setHours(0, 0, 0, 0)
    ).toISOString();

    const endFormatted = new Date(
        endOfCurrentWeek.setHours(23, 59, 59, 999)
    ).toISOString();

    return `${startFormatted},${endFormatted}`;
};

export function parseDaysHoursMinutesToSeconds(text?: string): number | null {
    if (!text) return null;
    const t = text.trim();
    const fullPattern = /^(?:(\d+)\s*h(?:\s*(\d+)\s*m(?:\s*(\d+)\s*s)?)?|\d+\s*m(?:\s*\d+\s*s)?|\d+\s*s)$/i;

    if (!fullPattern.test(text.trim())) {
        return null;
    }
  
    const match = /^(?:(\d+)\s*h)?(?:\s*(\d+)\s*m)?(?:\s*(\d+)\s*s)?$/i.exec(t)!;
    const hours   = parseInt(match[1] || '0', 10);
    const minutes = parseInt(match[2] || '0', 10);
    const seconds = parseInt(match[3] || '0', 10);
    return hours * 3600 + minutes * 60 + seconds;
}

export function formatSecondsToDaysHoursMinutes(valueInSeconds: number | undefined): string | undefined {
    if (valueInSeconds === undefined) return '0s';
    const duration = intervalToDuration({ start: 0, end: valueInSeconds * 1000 });

    const parts = [
        { value: duration.days, unit: 'd' },
        { value: duration.hours, unit: 'h' },
        { value: duration.minutes, unit: 'm' },
        { value: duration.seconds, unit: 's' }
    ];

    const result = parts
        .filter((part) => part.value && part.value > 0)
        .map((part) => `${part.value}${part.unit}`)
        .join(' ');

    return result || '0s';
}
function parseAndValidate(dateStr?: string): Date | null {
    if (!dateStr) return null;
    const date = parseISO(dateStr);
    return isValid(date) ? date : null;
}

function formatPart(value: number | undefined, suffix: string): string | null {
    return value ? `${value}${suffix}` : null;
}

function getFormattedParts(duration: Duration): string[] {
    return [
        formatPart(duration.days, 'd'),
        formatPart(duration.hours, 'h'),
        formatPart(duration.minutes, 'm'),
        formatPart(duration.seconds, 's')
    ].filter(Boolean) as string[];
}

export function getDurationString(date1Str: string, date2Str: string): string {
    const start = parseAndValidate(date1Str);
    const end = parseAndValidate(date2Str);
    if (!start || !end) return '0s';

    const duration = intervalToDuration({ start, end });
    const parts = getFormattedParts(duration);

    return parts.length > 0 ? parts.join(' ') : '0s';
}

export function formatDateToShort(dateStr: string, timezone?: string): string | undefined {
    if (!dateStr) return undefined;
    const date = parseISO(dateStr);
    if (!isValid(date)) return '';

    return dateTimeFormatterByTimezone(dateStr, 'HH:mm:ss dd/MM', timezone);
}
